import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import "../Revenue/revenue.css";
import "./withdrawals.css";
import { Avatar, Loading, Pager } from "../Revenue/parts";
import {
  ConfirmModal,
  CopyButton,
  fmtAgo,
  fmtTime,
  num,
  usd,
  useDebounced,
  useNotice,
} from "./payoutParts";
import {
  DETAIL_FIELDS,
  KYC_BADGE,
  METHOD_LABELS,
  OPEN_STATUSES,
  WD_STATUS_BADGE,
  WD_TABS,
  approveWithdrawal,
  doneMessage,
  getWithdrawal,
  listWithdrawals,
  markWithdrawalPaid,
  rejectWithdrawal,
  transitionMessage,
} from "./withdrawalsApi";

const LIMIT = 30;
const TAB_KEYS = WD_TABS.map((t) => t.key);

export function StatusBadge({ status }) {
  const b = WD_STATUS_BADGE[status] || { cls: "rv-badge-grey", text: status || "—" };
  return <span className={`rv-badge ${b.cls}`}>{b.text}</span>;
}

export function KycBadge({ status }) {
  const b = KYC_BADGE[status] || { cls: "rv-badge-grey", text: status ? `KYC ${status}` : "KYC —" };
  return <span className={`rv-badge ${b.cls}`}>{b.text}</span>;
}

function UserBlock({ user, size = "" }) {
  if (!user) return <span className="text-grey">—</span>;
  return (
    <div className="rv-user">
      <Avatar img={user.img} name={user.name} size={size} />
      <div style={{ minWidth: 0 }}>
        <div className="rv-user-name">
          {user.name ? `@${user.name}` : `User #${user.id}`}
          {user.deleted && <span className="rv-badge rv-badge-red ms-2">Deleted</span>}
        </div>
        <div className="rv-user-sub">
          #{user.id}
          {user.nick_name ? ` · ${user.nick_name}` : ""}
        </div>
      </div>
    </div>
  );
}

/// Diamond withdrawal queue: the admin pays each request by hand (PayPal,
/// bank, Western Union, Vodafone Cash) and records it here — approve, mark
/// paid with the transfer reference, or reject (the diamonds go back).
export default function Withdrawals() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(TAB_KEYS.includes(params.get("status")) ? params.get("status") : "pending");
  const [q, setQ] = useState(params.get("q") || "");
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState(null);
  const [counts, setCounts] = useState({});
  const [error, setError] = useState("");
  const [notice, setNotice] = useNotice();
  const [open, setOpen] = useState(null); // item shown in the drawer
  const seq = useRef(0);
  const term = useDebounced(q.trim(), 400);
  // The page belongs to one filter: a new tab / search starts at page 1.
  const filterKey = `${status}|${term}`;
  const [pager, setPager] = useState({ key: filterKey, page: 1 });
  const page = pager.key === filterKey ? pager.page : 1;

  const load = useCallback(async () => {
    const my = ++seq.current;
    setError("");
    try {
      const res = await listWithdrawals({ status, q: term, page, limit: LIMIT });
      if (my !== seq.current) return;
      setRows(res?.data || []);
      setMeta(res?.meta || null);
      setCounts(res?.counts || {});
    } catch (e) {
      if (my !== seq.current) return;
      setRows([]);
      setMeta(null);
      setError(transitionMessage(e, "Could not load the withdrawals"));
    }
  }, [status, term, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep the filter in the address (shareable, survives a refresh).
  useEffect(() => {
    const next = {};
    if (status !== "pending") next.status = status;
    if (term) next.q = term;
    setParams(next, { replace: true });
  }, [status, term, setParams]);

  const total = ["pending", "approved", "paid", "rejected", "legacy"].reduce((s, k) => s + (Number(counts[k]) || 0), 0);
  const countOf = (k) => (k === "all" ? total : Number(counts[k]) || 0);

  const onChanged = (item, msg) => {
    if (item) {
      setRows((list) => (list || []).map((r) => (r.id === item.id ? { ...r, ...item } : r)));
      setOpen((o) => (o && o.id === item.id ? { ...o, ...item } : o));
    }
    if (msg) setNotice(msg);
    load();
  };

  return (
    <div className="rv-wrap wd-page">
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-4">
        <div>
          <h2 className="rv-title mb-1">Withdrawals</h2>
          <p className="rv-sub">
            Hosts cash out diamonds. Send the money yourself (PayPal / bank / Western Union / Vodafone Cash), then
            record it here. Rejecting a request returns the diamonds to the user.
          </p>
        </div>
        <button type="button" className="rv-btn ghost" onClick={load}>
          Refresh
        </button>
      </div>

      {notice && <div className="rv-success mb-3">{notice}</div>}
      {error && <div className="rv-error mb-3">{error}</div>}

      <div className="rv-panel">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div className="rv-tabs">
            {WD_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`rv-tab ${status === t.key ? "active" : ""}`}
                onClick={() => setStatus(t.key)}
              >
                {t.label} <span className="wd-count">{num(countOf(t.key), 0)}</span>
              </button>
            ))}
          </div>
          <input
            className="rv-input wd-search"
            placeholder="Search name, nick name or ID"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {rows === null ? (
          <Loading />
        ) : !rows.length ? (
          <div className="rv-empty">{term ? "No request matches." : "No requests here."}</div>
        ) : (
          <div className="rv-table-wrap">
            <table className="rv-table wd-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th>KYC</th>
                  <th className="rv-num">Diamonds</th>
                  <th className="rv-num">USD</th>
                  <th className="rv-num">Rate</th>
                  <th>Method</th>
                  <th>Requested</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="rv-click" onClick={() => setOpen(r)}>
                    <td className="wd-id">{r.id}</td>
                    <td>
                      <UserBlock user={r.user} />
                    </td>
                    <td>
                      <KycBadge status={r.user?.kyc_status} />
                    </td>
                    <td className="rv-num">{num(r.diamonds)}</td>
                    <td className="rv-num">
                      <b>{usd(r.usd)}</b>
                    </td>
                    <td className="rv-num text-grey">{r.rate !== null && r.rate !== undefined ? usd(r.rate) : "—"}</td>
                    <td>{METHOD_LABELS[r.method] || r.method || "—"}</td>
                    <td>
                      <div>{fmtTime(r.requested_at)}</div>
                      <div className="rv-user-sub">{fmtAgo(r.requested_at)}</div>
                    </td>
                    <td>
                      <StatusBadge status={r.status} />
                      {r.legacy && r.status !== "completed" && <span className="rv-badge rv-badge-grey ms-1">Legacy</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pager meta={meta} onPage={(p) => setPager({ key: filterKey, page: p })} />
      </div>

      {open && <WithdrawalDrawer key={open.id} item={open} onClose={() => setOpen(null)} onChanged={onChanged} />}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="wd-row">
      <span>{label}</span>
      <span>{children}</span>
    </div>
  );
}

/// One request: payout details (copy buttons), the user's balances, the
/// history and the actions its status allows.
function WithdrawalDrawer({ item: initial, onClose, onChanged }) {
  const [item, setItem] = useState(initial);
  const [refreshing, setRefreshing] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null); // "paid" | "reject"
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const fresh = await getWithdrawal(initial.id);
      if (fresh) setItem(fresh);
      return fresh;
    } catch (e) {
      setError(transitionMessage(e, "Could not reload this request"));
      return null;
    } finally {
      setRefreshing(false);
    }
  }, [initial.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && !modal && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, modal, onClose]);

  const run = async (kind, call, verb) => {
    setBusy(kind);
    setError("");
    try {
      const res = await call();
      if (res?.data) setItem(res.data);
      setModal(null);
      onChanged(res?.data, doneMessage(res, verb));
    } catch (e) {
      setError(transitionMessage(e));
      const fresh = await refresh();
      if (fresh) onChanged(fresh, "");
      if (e?.response?.status === 409) setModal(null);
    } finally {
      setBusy("");
    }
  };

  const u = item.user || {};
  const details = item.details || {};
  const known = DETAIL_FIELDS.filter(([k]) => details[k] !== undefined && details[k] !== null && details[k] !== "");
  const extra = Object.keys(details).filter((k) => !DETAIL_FIELDS.some(([f]) => f === k));
  const isOpen = !item.legacy && OPEN_STATUSES.includes(item.status);
  const diamondsText = `${num(item.diamonds)} diamonds`;

  return (
    <div className="wd-drawer-backdrop" onClick={busy ? undefined : onClose}>
      <aside className="wd-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
          <div>
            <div className="rv-run-meta">Withdrawal #{item.id}</div>
            <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
              <StatusBadge status={item.status} />
              {item.legacy && <span className="rv-badge rv-badge-grey">Legacy · read only</span>}
              {refreshing && <span className="rv-run-meta">refreshing…</span>}
            </div>
          </div>
          <button type="button" className="wd-close" onClick={onClose} aria-label="Close" disabled={!!busy}>
            ×
          </button>
        </div>

        <UserBlock user={u} size="lg" />
        <div className="d-flex flex-wrap gap-2 mt-2">
          {u.id && (
            <Link className="rv-btn ghost rv-btn-sm" to={`/dashboard/user-management/${u.id}`}>
              Open user
            </Link>
          )}
          {u.id && (
            <Link className="rv-btn ghost rv-btn-sm" to={`/dashboard/wallet?type=user&id=${u.id}`}>
              Wallet
            </Link>
          )}
        </div>

        <div className="wd-amount">
          <div>
            <div className="rv-kpi-label">Pay</div>
            <div className="wd-amount-usd">{usd(item.usd)}</div>
          </div>
          <div className="text-end">
            <div className="rv-kpi-label">Diamonds</div>
            <div className="wd-amount-dm">{num(item.diamonds)}</div>
            <div className="rv-user-sub">{item.rate !== null && item.rate !== undefined ? `at ${usd(item.rate)} / diamond` : "no rate (legacy)"}</div>
          </div>
        </div>

        {u.kyc_status && u.kyc_status !== "allowed" && (
          <div className="wd-warn mt-3">
            The user's KYC is now <b>{KYC_BADGE[u.kyc_status]?.text.replace("KYC ", "") || u.kyc_status}</b>. New
            requests need “allowed”. Check the ID on the <Link to="/dashboard/kyc">KYC page</Link> before paying.
          </div>
        )}
        {item.legacy && (
          <div className="wd-info mt-3">
            Recorded before the withdrawal queue existed (2026-10-22). Nothing to approve, pay or refund here.
          </div>
        )}

        <div className="wd-section">Payout — {METHOD_LABELS[item.method] || item.method || "method unknown"}</div>
        {known.length || extra.length ? (
          <div className="wd-details">
            {known.map(([k, label]) => (
              <div key={k} className="wd-detail">
                <span className="wd-detail-label">{label}</span>
                <span className="wd-detail-value">{String(details[k])}</span>
                <CopyButton value={details[k]} />
              </div>
            ))}
            {extra.map((k) => (
              <div key={k} className="wd-detail">
                <span className="wd-detail-label">{k}</span>
                <span className="wd-detail-value">{String(details[k])}</span>
                <CopyButton value={details[k]} />
              </div>
            ))}
            {item.usd !== null && item.usd !== undefined && (
              <div className="wd-detail">
                <span className="wd-detail-label">Amount (USD)</span>
                <span className="wd-detail-value">{item.usd}</span>
                <CopyButton value={item.usd} />
              </div>
            )}
          </div>
        ) : (
          <div className="rv-empty py-3">No payout details on this request.</div>
        )}

        <div className="wd-section">User balances (now)</div>
        <Row label="Diamonds">{num(u.diamonds)}</Row>
        <Row label="Coins">
          <span className={Number(u.coins) < 0 ? "rv-amount-neg" : ""}>{num(u.coins)}</span>
        </Row>
        <Row label="KYC">
          <KycBadge status={u.kyc_status} />
        </Row>

        <div className="wd-section">History</div>
        <div className="wd-timeline">
          <TimelineItem label="Requested" at={item.requested_at} />
          {item.approved_at && <TimelineItem label="Approved" at={item.approved_at} by={item.approved_by} />}
          {item.paid_at && (
            <TimelineItem label="Paid" at={item.paid_at} by={item.paid_by} tone="green">
              {item.paid_reference && (
                <div className="wd-tl-extra">
                  Reference: <b>{item.paid_reference}</b> <CopyButton value={item.paid_reference} />
                </div>
              )}
              {item.paid_note && <div className="wd-tl-extra">Note: {item.paid_note}</div>}
            </TimelineItem>
          )}
          {item.rejected_at && (
            <TimelineItem label="Rejected" at={item.rejected_at} by={item.rejected_by} tone="red">
              {item.reject_reason && <div className="wd-tl-extra">Reason: {item.reject_reason}</div>}
              {item.refunded_diamonds !== null && item.refunded_diamonds !== undefined && (
                <div className="wd-tl-extra">Returned to the user: {num(item.refunded_diamonds)} diamonds</div>
              )}
            </TimelineItem>
          )}
        </div>

        {error && <div className="rv-error mt-3">{error}</div>}

        {isOpen && (
          <div className="wd-actions">
            {item.status === "pending" && (
              <button
                type="button"
                className="rv-btn violet"
                disabled={!!busy}
                onClick={() => run("approve", () => approveWithdrawal(item.id), "approved")}
              >
                {busy === "approve" ? "Approving…" : "Approve"}
              </button>
            )}
            <button type="button" className="rv-btn success" disabled={!!busy} onClick={() => setModal("paid")}>
              Mark paid
            </button>
            <button type="button" className="rv-btn danger" disabled={!!busy} onClick={() => setModal("reject")}>
              Reject
            </button>
          </div>
        )}

        {modal === "paid" && (
          <ConfirmModal
            title={`Mark #${item.id} as paid`}
            confirmText="Mark paid"
            tone="success"
            busy={busy === "paid"}
            error={busy ? "" : error}
            onClose={() => setModal(null)}
            onConfirm={() => run("paid", () => markWithdrawalPaid(item.id, { reference, note }), "marked as paid")}
          >
            <p className="rv-sub mb-3">
              Only after you have sent <b className="text-white">{usd(item.usd)}</b> by{" "}
              {METHOD_LABELS[item.method] || item.method || "the chosen method"}. The user is notified that the money
              was sent.
            </p>
            <label className="rv-label">Transfer reference (optional)</label>
            <input
              className="rv-input"
              maxLength={191}
              value={reference}
              autoFocus
              placeholder="e.g. PayPal transaction ID"
              onChange={(e) => setReference(e.target.value)}
            />
            <label className="rv-label mt-3">Note (optional, admins only)</label>
            <textarea className="rv-textarea" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
          </ConfirmModal>
        )}

        {modal === "reject" && (
          <ConfirmModal
            title={`Reject #${item.id}`}
            confirmText="Reject and return diamonds"
            tone="danger"
            busy={busy === "reject"}
            disabled={reason.trim().length < 2}
            error={busy ? "" : error}
            onClose={() => setModal(null)}
            onConfirm={() => run("reject", () => rejectWithdrawal(item.id, reason), "rejected — the diamonds went back to the user")}
          >
            <p className="rv-sub mb-3">
              The <b className="text-white">{diamondsText}</b> go back to the user's diamond balance and the user is
              notified with this reason. This cannot be undone (the user can request again).
            </p>
            <label className="rv-label">Reason (required, the user sees it)</label>
            <textarea
              className="rv-textarea"
              rows={3}
              maxLength={500}
              value={reason}
              autoFocus
              placeholder="e.g. The PayPal email does not exist"
              onChange={(e) => setReason(e.target.value)}
            />
            <div className="wd-hint">{reason.trim().length}/500</div>
          </ConfirmModal>
        )}
      </aside>
    </div>
  );
}

function TimelineItem({ label, at, by, tone = "", children }) {
  return (
    <div className={`wd-tl ${tone}`}>
      <div className="wd-tl-head">
        <b>{label}</b>
        <span className="rv-run-meta">
          {fmtTime(at)}
          {by ? ` · admin #${by}` : ""}
        </span>
      </div>
      {children}
    </div>
  );
}
